import { Application, Container, Sprite, Texture } from 'pixi.js';
import type {
  BookOptions,
  BoundFlipAttemptEvent,
  DisplayMode,
  FlipCorner,
  FlipSource,
  ManualFlipEvent,
  ManualFlipRestoreEvent,
  ManualFlipStartEvent,
  PageChangeEvent
} from '@/types/book';
import { FlipController } from './renderer/FlipController';
import { PixiBookRenderer } from './renderer/PixiBookRenderer';
import { PixiPage } from './page/PixiPage';
import {
  computeContentSize,
  computeVisibleBookLayout
} from '@/modules/editor/canvas/zoom/zoomMath';
import { normalizeSpreadPageIndex } from './display/pageTransition';

export class BookCore {
  private readonly options: BookOptions;
  private renderer!: PixiBookRenderer;
  private controller!: FlipController;
  private app: Application | null = null;
  private _onPageChange: ((event: PageChangeEvent) => void) | null = null;
  private _onManualFlip: ((event: ManualFlipEvent) => void) | null = null;
  private _onManualFlipStart: ((event: ManualFlipStartEvent) => void) | null = null;
  private _onManualFlipRestore: ((event: ManualFlipRestoreEvent) => void) | null = null;
  private _onBoundFlipAttempt: ((event: BoundFlipAttemptEvent) => void) | null = null;
  private readonly pageChangeListeners = new Set<(event: PageChangeEvent) => void>();
  private interactionEnabled = true;
  private manualFlipBlocked = false;
  private clickFlipEnabled = true;

  constructor(_options: BookOptions) {
    this.options = _options;
    this._onPageChange = _options.onPageChange ?? null;
    this._onManualFlip = _options.onManualFlip ?? null;
    this._onManualFlipStart = _options.onManualFlipStart ?? null;
    this._onManualFlipRestore = _options.onManualFlipRestore ?? null;
    this._onBoundFlipAttempt = _options.onBoundFlipAttempt ?? null;
  }

  get root(): Container | undefined {
    return this.renderer?.root;
  }

  get pages(): readonly PixiPage[] {
    return this.renderer?.pages ?? [];
  }

  get pageCount(): number {
    return this.renderer?.pages.length ?? 0;
  }

  get currentPageIndex(): number {
    return this.renderer?.getCurrentPageIndex() ?? 0;
  }

  get pageWidth(): number {
    return this.options.pageWidth;
  }

  get pageHeight(): number {
    return this.options.pageHeight;
  }

  isAnimating(): boolean {
    return this.renderer?.isAnimating() ?? false;
  }

  get displayMode() {
    return this.options.displayMode ?? 'double';
  }

  /** 书本内容尺寸（双页模式宽度翻倍），供缩放控制器做边界计算。 */
  get contentSize(): { width: number; height: number } {
    return computeContentSize(this.options.pageWidth, this.options.pageHeight, this.displayMode);
  }

  /** 当前跨页可见区域与 centerContainer 偏移，供缩放贴合计算。 */
  get visibleLayout(): { width: number; height: number; offsetX: number; offsetY: number } {
    return computeVisibleBookLayout(
      this.options.pageWidth,
      this.options.pageHeight,
      this.displayMode,
      this.currentPageIndex,
      this.pageCount
    );
  }

  set onPageChange(handler: ((event: PageChangeEvent) => void) | null) {
    this._onPageChange = handler;
  }

  set onManualFlip(handler: ((event: ManualFlipEvent) => void) | null) {
    this._onManualFlip = handler;
  }

  set onManualFlipStart(handler: ((event: ManualFlipStartEvent) => void) | null) {
    this._onManualFlipStart = handler;
  }

  set onManualFlipRestore(handler: ((event: ManualFlipRestoreEvent) => void) | null) {
    this._onManualFlipRestore = handler;
  }

  set onBoundFlipAttempt(handler: ((event: BoundFlipAttemptEvent) => void) | null) {
    this._onBoundFlipAttempt = handler;
  }

  setInteractionEnabled(enabled: boolean): void {
    this.interactionEnabled = enabled;
    this.controller?.setEnabled(enabled);
  }

  isInteractionEnabled(): boolean {
    return this.interactionEnabled;
  }

  /** 阻断用户翻页输入，不影响程序翻页和画布上的其它交互。 */
  setManualFlipBlocked(blocked: boolean): void {
    this.manualFlipBlocked = blocked;
    this.controller?.setManualFlipBlocked(blocked);
  }

  /** 控制静止短按是否提交翻页；拖拽翻页不受影响。 */
  setClickFlipEnabled(enabled: boolean): void {
    this.clickFlipEnabled = enabled;
    this.controller?.setClickFlipEnabled(enabled);
  }

  /**
   * 判断浏览器 client 坐标是否落在四角自动翻页区域。
   * 供外部（如阅读端的点击翻页）排除四角，避免与 FlipController 的角自动翻页重复触发导致双翻。
   * 复用渲染器 canStartAt：其内含方向可翻性检查，与 isFirstPage/isLastPage 守卫天然对齐。
   */
  isPointOnFlipCorner(clientX: number, clientY: number): boolean {
    if (!this.renderer) return false;
    return this.renderer.canStartAt(this.renderer.clientToBook(clientX, clientY));
  }

  /** 通知构造时的主回调以及所有额外注册的监听器（用于 BookCanvas 等外部同步）。 */
  private notifyPageChange(event: PageChangeEvent): void {
    this._onPageChange?.(event);
    this.pageChangeListeners.forEach((listener) => listener(event));
  }

  /** 注册额外的页码变化监听器，不影响构造时传入的 onPageChange。 */
  addPageChangeListener(listener: (event: PageChangeEvent) => void): void {
    this.pageChangeListeners.add(listener);
  }

  /** 移除之前注册的页码变化监听器。 */
  removePageChangeListener(listener: (event: PageChangeEvent) => void): void {
    this.pageChangeListeners.delete(listener);
  }

  mount(parent: Container, app: Application): void {
    this.createRenderer(app);
    parent.addChild(this.renderer.root);
    this.renderer.refresh();
  }

  private createRenderer(app: Application): void {
    this.renderer = new PixiBookRenderer({
      app,
      pageWidth: this.options.pageWidth,
      pageHeight: this.options.pageHeight,
      pageRadius: this.options.pageRadius,
      displayMode: this.options.displayMode,
      coverFlipType: this.options.coverFlipType,
      onPageChange: (event) => this.notifyPageChange(event),
      onManualFlip: (event) => this._onManualFlip?.(event),
      onManualFlipStart: (event) => this._onManualFlipStart?.(event),
      onManualFlipRestore: (event) => this._onManualFlipRestore?.(event),
      onBoundFlipAttempt: (event) => this._onBoundFlipAttempt?.(event),
      onPageErrorEdit: this.options.onPageErrorEdit,
      onPageErrorRetry: this.options.onPageErrorRetry,
      pageErrorEditLabel: this.options.pageErrorEditLabel
    });
    this.controller = new FlipController(this.renderer);
    this.controller.setEnabled(this.interactionEnabled);
    this.controller.setManualFlipBlocked(this.manualFlipBlocked);
    this.controller.setClickFlipEnabled(this.clickFlipEnabled);
  }

  /** 从父容器或 DOM 卸载，保留 renderer 状态可再次 mount。 */
  unmount(): void {
    if (!this.renderer) return;
    this.renderer.root.removeFromParent();
    if (this.app) {
      const canvas = this.app.canvas as HTMLCanvasElement;
      canvas.remove();
    }
  }

  /** 销毁 Book 实例（终态清理）。 */
  destroy(): void {
    this.controller?.destroy();
    this.controller = null!;
    this.pageChangeListeners.clear();
    this.renderer?.destroy();
    this.renderer = null!;
    if (this.app) {
      this.app.destroy(true);
      this.app = null;
    }
  }

  // ── 页面管理 ──

  /** 在末尾追加一页，自动重建书脊阴影。 */
  addPage(): PixiPage {
    const index = this.renderer.pages.length;
    const page = new PixiPage(
      index,
      this.options.pageWidth,
      this.options.pageHeight,
      this.options.pageRadius,
      this.options.onPageErrorEdit,
      this.options.pageErrorEditLabel,
      this.options.onPageErrorRetry
    );
    this.renderer.pages.push(page);
    this.rebuildAllSpineShadows();
    this.renderer.refresh();
    return page;
  }

  ensurePageCount(pageCount: number): void {
    if (!this.renderer) return;

    while (this.renderer.pages.length < pageCount) {
      this.addPage();
    }
  }

  refresh(): void {
    this.renderer?.refresh();
  }

  /**
   * 用最终显示像素重建翻页几何，同时保留 BookCore 实例、当前页与已挂载页面内容。
   * 页面内容会先从旧 PixiPage 脱离，避免旧 renderer 销毁时一并销毁业务 BookPage。
   */
  resize(
    pageWidth: number,
    pageHeight: number,
    displayMode: DisplayMode = this.displayMode
  ): boolean {
    if (pageWidth <= 0 || pageHeight <= 0) return false;
    if (
      this.options.pageWidth === pageWidth &&
      this.options.pageHeight === pageHeight &&
      this.displayMode === displayMode
    )
      return false;

    const oldPageWidth = this.options.pageWidth;
    const oldRenderer = this.renderer;
    if (!oldRenderer) {
      this.options.pageWidth = pageWidth;
      this.options.pageHeight = pageHeight;
      this.options.displayMode = displayMode;
      return true;
    }

    const app = oldRenderer.options.app;
    const parent = oldRenderer.root.parent;
    const parentIndex = parent ? parent.children.indexOf(oldRenderer.root) : -1;
    const currentPageIndex = oldRenderer.getCurrentPageIndex();
    const pageStates = oldRenderer.pages.map((page) => ({
      needSpineShadow: page.needSpineShadow,
      content: page.content.removeChildren()
    }));

    this.controller?.destroy();
    oldRenderer.root.removeFromParent();
    oldRenderer.destroy();

    const radiusScale = oldPageWidth > 0 ? pageWidth / oldPageWidth : 1;
    this.options.pageWidth = pageWidth;
    this.options.pageHeight = pageHeight;
    this.options.displayMode = displayMode;
    if (this.options.pageRadius !== undefined) {
      this.options.pageRadius *= radiusScale;
    }

    this.createRenderer(app);
    if (parent) {
      const insertAt = Math.max(0, Math.min(parentIndex, parent.children.length));
      parent.addChildAt(this.renderer.root, insertAt);
    }

    this.ensurePageCount(pageStates.length);
    pageStates.forEach((state, index) => {
      const page = this.renderer.pages[index];
      if (!page) return;
      page.needSpineShadow = state.needSpineShadow;
      if (state.content.length > 0) page.content.addChild(...state.content);
    });
    this.rebuildAllSpineShadows();
    const nextPageIndex =
      displayMode === 'single'
        ? Math.max(0, currentPageIndex)
        : normalizeSpreadPageIndex(currentPageIndex, displayMode, pageStates.length);
    this.renderer.setCurrentPageIndex(nextPageIndex);
    this.renderer.refresh();
    return true;
  }

  /** 按索引获取页面。 */
  getPage(index: number): PixiPage | undefined {
    if (!this.renderer) return undefined;

    return this.renderer.pages[index];
  }

  /** 设置指定页面是否需要书脊阴影，并触发该页阴影重建。 */
  setPageNeedSpineShadow(index: number, needSpineShadow: boolean): void {
    if (!this.renderer) return;
    this.renderer.setPageNeedSpineShadow(index, needSpineShadow);
  }

  setPageErrorEditLabel(label?: string): void {
    this.options.pageErrorEditLabel = label;
    this.renderer?.pages.forEach((page) => page.setErrorEditLabel(label));
  }

  /** 删除指定页，后续页向前移位并重建阴影。 */
  removePage(index: number): void {
    if (this.renderer.pages.length <= 1) return;
    this.renderer.pages.splice(index, 1);
    for (let i = index; i < this.renderer.pages.length; i++) {
      this.renderer.pages[i].index = i;
    }
    this.rebuildAllSpineShadows();
    const cur = this.renderer.getCurrentPageIndex();
    if (cur >= index && cur > 0) {
      this.renderer.setCurrentPageIndex(cur - 1);
    }
    this.renderer.refresh();
  }

  /** 在指定位置插入一页，后续页向后移位并重建阴影。 */
  insertPage(index: number): PixiPage {
    const page = new PixiPage(
      index,
      this.options.pageWidth,
      this.options.pageHeight,
      this.options.pageRadius,
      this.options.onPageErrorEdit,
      this.options.pageErrorEditLabel,
      this.options.onPageErrorRetry
    );
    this.renderer.pages.splice(index, 0, page);
    for (let i = index; i < this.renderer.pages.length; i++) {
      this.renderer.pages[i].index = i;
    }
    this.rebuildAllSpineShadows();
    if (index <= this.renderer.getCurrentPageIndex()) {
      this.renderer.setCurrentPageIndex(this.renderer.getCurrentPageIndex() + 1);
    }
    this.renderer.refresh();
    return page;
  }

  /** 重建所有页面的书脊阴影（增删页后调用，页数少所以全量重建）。 */
  private rebuildAllSpineShadows(): void {
    for (let i = 0; i < this.renderer.pages.length; i++) {
      this.renderer.rebuildSpineShadow(i);
    }
  }

  // ── 页面内容 ──

  /** 用 Pixi Texture 替换指定页面的内容。 */
  setPageTexture(index: number, texture: Texture): void {
    const page = this.renderer.pages[index];
    if (!page) return;
    page.content.removeChildren();
    const sprite = new Sprite(texture);
    sprite.width = this.options.pageWidth;
    sprite.height = this.options.pageHeight;
    page.content.addChild(sprite);
  }

  /** 用 Canvas 纹理替换指定页面的内容。 */
  setPageCanvas(index: number, canvas: HTMLCanvasElement): void {
    const texture = Texture.from(canvas);
    this.setPageTexture(index, texture);
  }

  // ── 导航 ──

  next(corner: FlipCorner = 'bottom', source: FlipSource = 'programmatic'): void {
    this.renderer.next(corner, false, source);
  }

  prev(corner: FlipCorner = 'bottom', source: FlipSource = 'programmatic'): void {
    this.renderer.prev(corner, false, source);
  }

  first(): void {
    this.renderer.first();
  }

  last(): void {
    this.renderer.last();
  }

  flipToPage(pageIndex: number, progress: number = 0.5, corner: FlipCorner = 'bottom'): void {
    this.renderer.flipToPage(pageIndex, progress, corner);
  }

  flipBetweenPages(
    fromPageIndex: number,
    toPageIndex: number,
    progress: number,
    corner: FlipCorner = 'bottom'
  ): void {
    this.renderer.flipBetweenPages(fromPageIndex, toPageIndex, progress, corner);
  }
}
